class CartDrawer extends DrawerComponent {
  constructor() {
    super();
    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.getSectionToRenderListener = this.getSectionToRender.bind(this);
    this.handleScrollListener = this.handleScroll.bind(this);
  }

  get requiresBodyAppended() {
    return false;
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.addEventListener('cart:refresh', this.onCartRefreshListener);
    window.addEventListener('scroll', this.handleScrollListener);

    // Footer is re-rendered via innerHTML on cart updates, so the modal's own listeners get lost
    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[aria-controls^="PaymentInstallmentsModal-"]');
        if (!button || !this.contains(button)) return;

        const modal = document.getElementById(button.getAttribute('aria-controls'));
        if (!modal || typeof modal.show !== 'function') return;

        event.preventDefault();
        modal.show(button);
      },
      { signal: this.abortController.signal }
    );
    
    // Set initial margin-top based on current scroll position
    this.updateMarginTop();
    
    // Initialize terms and conditions functionality
    this.initTermsAndConditions();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:grouped-sections', this.getSectionToRenderListener);
    document.removeEventListener('cart:refresh', this.onCartRefreshListener);
    window.removeEventListener('scroll', this.handleScrollListener);
  }

  handleScroll() {
    this.updateMarginTop();
  }

  updateMarginTop() {
    const isAtTop = window.scrollY === 0;
    const fixedOverlay = this.querySelector('.fixed-overlay');
    
    if (fixedOverlay) {
      if (isAtTop) {
        fixedOverlay.style.marginTop = '37px';
      } else {
        fixedOverlay.style.marginTop = '0px';
      }
    }
    
    // Apply margin-top to the cart-drawer element itself (this element)
    if (isAtTop) {
      this.style.marginTop = '37px';
    } else {
      this.style.marginTop = '0px';
    }
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  show(focusElement = null, animate = true) {
    super.show(focusElement, animate);

    if (this.open && !Shopify.designMode) {
      FoxTheme.a11y.trapFocus(this, this.focusElement);
    }
  }

  async onCartRefresh(event) {
    const cartId = `CartDrawer-${this.sectionId}`;

    const cartElement = document.getElementById(cartId);
    if (!cartElement) return;

    try {
      const response = await fetch(`${FoxTheme.routes.root_url}?section_id=${this.sectionId}`);
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, 'text/html');

      const newCartContent = parsedHTML.getElementById(cartId).innerHTML;
      cartElement.innerHTML = newCartContent;

      // Reinitialize terms and conditions after cart refresh
      this.initTermsAndConditions();

      if (event.detail.open === true) {
        this.show();
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  }

  initTermsAndConditions() {
    const termsCheckbox = this.querySelector('#terms-and-conditions');
    const checkoutButton = this.querySelector('.btn.btn--primary.flex-grow');
    
    if (!termsCheckbox || !checkoutButton) return;
    
    // Get popup elements
    const popup = document.querySelector(`#TermsWarningPopup-${this.sectionId}`);
    const popupOverlay = popup?.querySelector('.terms-warning-overlay');
    const popupCloseBtn = popup?.querySelector('.terms-warning-close');
        
    // Function to show popup
    const showTermsWarningPopup = () => {
      if (popup) {
        popup.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
        
        // Focus on close button for accessibility
        setTimeout(() => {
          popupCloseBtn?.focus();
        }, 100);
      } 
    };
    
    // Function to hide popup
    const hideTermsWarningPopup = () => {
      if (popup) {
        popup.classList.add('hidden');
        document.body.style.overflow = '';
      }
    };
    
    // Function to update button state
    const updateButtonState = () => {
      if (termsCheckbox.checked) {
        checkoutButton.disabled = false;
        checkoutButton.style.opacity = '1';
        checkoutButton.style.cursor = 'pointer';
        checkoutButton.style.pointerEvents = 'auto';
      } else {
        // Don't disable the button - instead keep it enabled but prevent default action
        checkoutButton.disabled = false;
        checkoutButton.style.opacity = '0.5';
        checkoutButton.style.cursor = 'not-allowed';
        checkoutButton.style.pointerEvents = 'auto';
      }
    };
    
    // Set initial state
    updateButtonState();
    
    // Add event listener for checkbox changes
    termsCheckbox.addEventListener('change', updateButtonState);
    
    // Add event listeners for popup
    if (popup) {
      // Close popup when clicking close button
      popupCloseBtn?.addEventListener('click', hideTermsWarningPopup);
      
      // Close popup when clicking overlay
      popupOverlay?.addEventListener('click', hideTermsWarningPopup);
      
      // Close popup with Escape key
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !popup.classList.contains('hidden')) {
          hideTermsWarningPopup();
        }
      });
    }
    
    // Add click event listener to checkout button for popup
    checkoutButton.addEventListener('click', (event) => {
      if (!termsCheckbox.checked) {
        event.preventDefault();
        event.stopPropagation();
        showTermsWarningPopup();
        return false;
      }
    });
    
    // Prevent form submission if terms are not accepted
    const form = checkoutButton.closest('form');
    if (form) {
      form.addEventListener('submit', (event) => {
        if (!termsCheckbox.checked) {
          event.preventDefault();
          event.stopPropagation();
          showTermsWarningPopup();
          return false;
        }
      });
    }
  }
}
customElements.define('cart-drawer', CartDrawer);
class CartItems extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      this.onCartUpdate.bind(this)
    );

    this.cartItemProducts = this.querySelectorAll('.cart-item__product');
    this.cartItemQuantitys = this.querySelectorAll('.cart-item__quantity');

    const loadTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const template = element.querySelector('template');
        const templateContent = template && document.importNode(template.content, true);
        const parent = element.querySelector(parentSelector);

        if (parent && template && !parent.querySelector('.template-content')) {
          const contentWrapper = document.createElement('div');
          contentWrapper.classList.add('template-content');
          contentWrapper.appendChild(templateContent);
          parent.appendChild(contentWrapper);
        }
      });
    };

    const removeTemplateContent = (elements, parentSelector) => {
      elements.forEach((element) => {
        const parent = element.querySelector(parentSelector);
        const templateContent = parent?.querySelector('.template-content');

        if (templateContent) {
          parent.removeChild(templateContent);
        }
      });
    };

    const handleTabletMatch = () => {
      loadTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      removeTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const handleTabletUnmatch = () => {
      removeTemplateContent(this.cartItemProducts, '.cart-item__product--info');
      loadTemplateContent(this.cartItemQuantitys, '.cart-item__quantity-wrapper');
    };

    const mqlTablet = window.matchMedia(FoxTheme.config.mediaQueryTablet);
    FoxTheme.config.mqlTablet = mqlTablet.matches;

    if (FoxTheme.config.mqlTablet) {
      handleTabletMatch();
    } else {
      handleTabletUnmatch();
    }

    mqlTablet.onchange = (event) => {
      if (event.matches) {
        handleTabletMatch();
      } else {
        FoxTheme.config.mqlTablet = false;
        handleTabletUnmatch();
      }
    };

    window.FoxKitSections = FoxTheme.utils.getSectionId(this);
  }

  cartUpdateUnsubscriber = undefined;

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target
    );
  }

  onCartError(errors, target) {
    if (target) {
      this.hideLoader(target.getAttribute('data-index'));
      this.setValidity(target, errors);
      return;
    }
    
    // Get the appropriate error message (custom or auto)
    const finalErrorMessage = this.getErrorMessage(errors);
    
    // Check the stock limit display method setting
    const displayMethod = FoxTheme?.settings?.stockLimitDisplayMethod || 'text';
    
    if (displayMethod === 'popup') {
      // Show popup modal instead of alert
      const stockErrorModal = document.getElementById('StockErrorModal');
      if (stockErrorModal) {
        stockErrorModal.show(finalErrorMessage);
        return;
      }
    }
    
    // Default behavior - redirect or show alert
    if (finalErrorMessage) {
      alert(finalErrorMessage);
    } else {
      window.location.href = FoxTheme.routes.cart_url;
    }
  }

  setValidity(target, message) {
    // ekranda çıkan browser validasyonu
    /* target.setCustomValidity(message); */
    target.reportValidity();
    target.value = target.defaultValue;
    target.select();
  }

  // Helper function to get the appropriate error message
  getErrorMessage(originalMessage) {
    const messageSource = FoxTheme?.settings?.stockErrorMessageSource || 'auto';
    
    if (messageSource === 'custom') {
      const customMessage = FoxTheme?.settings?.stockErrorCustomMessage;
      return customMessage && customMessage.trim() !== '' ? customMessage : originalMessage;
    }
    
    return originalMessage;
  }

  async onCartUpdate(event) {
    if (event.cart.errors) {
      this.onCartError(event.cart.errors, event.target);
      return;
    }

    const sectionId = FoxTheme.utils.getSectionId(this);
    let manuel_section = {}
    const data = await fetch(window.Shopify.routes.root + `?sections=${sectionId}`)
    const text = await data.json()
    manuel_section = text
    let sectionToRender = ""
    if(event.cart.sections != undefined){
      sectionToRender = new DOMParser().parseFromString(event.cart.sections[sectionId], 'text/html');
    }else{
      sectionToRender = new DOMParser().parseFromString(manuel_section[sectionId], 'text/html');
    }

    const cartDrawer = document.querySelector(`#CartDrawer-${sectionId}`);
    const cartDrawerBody = document.querySelector(`#CartDrawerBody-${sectionId}`);
    const cartDrawerFooter = document.querySelector(`#CartDrawerFooter-${sectionId}`);
    const cartDrawerEmpty = document.querySelector(`#CartDrawerEmpty-${sectionId}`);
    if (cartDrawer) {
      const cartDrawerBodyUpdate = sectionToRender.querySelector(`#CartDrawerBody-${sectionId}`);
      const cartDrawerFooterUpdate = sectionToRender.querySelector(`#CartDrawerFooter-${sectionId}`);
      const cartDrawerEmptyUpdate = sectionToRender.querySelector(`#CartDrawerEmpty-${sectionId}`);

      if (cartDrawerBodyUpdate) {
        cartDrawerBody.innerHTML = cartDrawerBodyUpdate.innerHTML;
      }
      if (cartDrawerFooterUpdate) {
        cartDrawerFooter.innerHTML = cartDrawerFooterUpdate.innerHTML;
        
        // Reinitialize terms and conditions after footer update
        const cartDrawerElement = cartDrawer.closest('cart-drawer');
        if (cartDrawerElement && cartDrawerElement.initTermsAndConditions) {
          cartDrawerElement.initTermsAndConditions();
        }
      }

      if (cartDrawerEmptyUpdate) {
        cartDrawerEmpty.innerHTML = cartDrawerEmptyUpdate.innerHTML;
      }

      if (event.cart.item_count > 0) {
        cartDrawerBody.classList.remove('hidden');
        cartDrawerFooter.classList.remove('hidden');
        cartDrawerEmpty.classList.add('hidden');
      } else {
        cartDrawerBody.classList.add('hidden');
        cartDrawerFooter.classList.add('hidden');
        cartDrawerEmpty.classList.remove('hidden');
      }
    }

    const mainCart = document.querySelector(`#MainCart-${sectionId}`);
    if (mainCart) {
      const updatedElement = sectionToRender.querySelector(`#MainCart-${sectionId}`);
      if (updatedElement) {
        mainCart.innerHTML = updatedElement.innerHTML;
        if (event.cart.item_count > 0) {
          mainCart.closest('.cart').classList.remove('is-empty');
        } else {
          mainCart.closest('.cart').classList.add('is-empty');
        }
        
        // Reinitialize terms and conditions for main cart after update
        const mainCartElement = mainCart.closest('main-cart');
        if (mainCartElement && mainCartElement.initTermsAndConditions) {
          mainCartElement.initTermsAndConditions();
        }
      } else {
        mainCart.closest('.cart').classList.add('is-empty');
        mainCart.remove();
      }
    }

    const lineItem =
      document.getElementById(`CartItem-${event.line}`) || document.getElementById(`CartDrawer-Item-${event.line}`);

    if (lineItem && lineItem.querySelector(`[name="${event.name}"]`)) {
      FoxTheme.a11y.trapFocus(mainCart || cartDrawer, lineItem.querySelector(`[name="${event.name}"]`));
    } else if (event.cart.item_count === 0) {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('a'))
        : FoxTheme.a11y.trapFocus(document.querySelector('.cart__empty'), document.querySelector('a'));
    } else {
      cartDrawer
        ? FoxTheme.a11y.trapFocus(cartDrawer, cartDrawer.querySelector('.cart-item__title'))
        : FoxTheme.a11y.trapFocus(mainCart, mainCart.querySelector('.cart-item__title'));
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: event.cart,
        },
      })
    );
  }

  async updateQuantity(line, quantity, name, target) {
    try {
      // Check cart limit before updating quantity
      const canUpdate = await this.checkCartLimitBeforeQuantityUpdate(line, quantity, target);
      if (!canUpdate) {
        this.showCartLimitError(target);
        return;
      }

      this.showLoader(line);

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        line,
        quantity,
        sections: sectionsToBundle,
      });

      fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((parsedState) => {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState, target, line, name });
        })
        .catch((error) => {
          console.log(error);
        });
    } catch (error) {
      console.error('Error checking cart limit for quantity update:', error);
      // Proceed with update if check fails
      this.showLoader(line);

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        line,
        quantity,
        sections: sectionsToBundle,
      });

      fetch(`${FoxTheme.routes.cart_change_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((parsedState) => {
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState, target, line, name });
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }

  showLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.add('btn--loading');
      });
    }
  }

  hideLoader(line) {
    const sectionId = FoxTheme.utils.getSectionId(this);
    const loaders = document.querySelectorAll(`#Loader-${sectionId}-${line}`);
    if (loaders) {
      loaders.forEach((loader) => {
        loader.classList.remove('btn--loading');
      });
    }
  }

  async checkCartLimitBeforeQuantityUpdate(line, newQuantity, target) {
    try {
      // Get current cart data
      const currentCartJson = await (await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })).json();
      
      // Find the item being updated
      const itemIndex = parseInt(line) - 1; // line is 1-based, array is 0-based
      const currentItem = currentCartJson.items[itemIndex];
      
      if (!currentItem) {
        return true; // Allow update if item not found
      }
      
      // Store the original quantity for potential revert
      if (target && target.tagName === 'INPUT') {
        target.dataset.originalQuantity = currentItem.quantity;
      }
      
      // Calculate the quantity difference
      const currentQuantity = currentItem.quantity;
      const quantityDifference = newQuantity - currentQuantity;
      
      // If quantity is decreasing or staying the same, allow it
      if (quantityDifference <= 0) {
        return true;
      }
      
      // Calculate the price difference (only for the increase)
      const itemPrice = currentItem.price; // Price per unit in cents
      const priceDifference = itemPrice * quantityDifference;
      
      // Calculate new cart total
      const currentTotal = currentCartJson.total_price || 0;
      const newCartTotal = currentTotal + priceDifference;
      
      // Check if cart limit is enabled and get the limit amount
      const isCartLimitEnabled = FoxTheme?.settings?.enableCartLimit === true;
      if (!isCartLimitEnabled) {
        return true; // Allow update if cart limit is disabled
      }
      
      // Get cart limit from settings (convert TL to cents)
      const cartLimitTL = FoxTheme?.settings?.cartLimitAmount || 150000;
      const CART_LIMIT = cartLimitTL * 100;
      
      const withinLimit = newCartTotal <= CART_LIMIT;      
      return withinLimit;
    } catch (error) {
      return true; // Allow update if check fails
    }
  }

  showCartLimitError(target = null) {
    // Revert the quantity input to its original value if target is provided
    if (target && target.tagName === 'INPUT' && target.dataset.originalQuantity) {
      target.value = target.dataset.originalQuantity;
      // Clean up the temporary data
      delete target.dataset.originalQuantity;
    }
    
    // Create or show cart limit error popup
    let popup = document.getElementById('CartLimitErrorPopup');
    
    if (!popup) {
      // Create popup if it doesn't exist
      popup = document.createElement('div');
      popup.id = 'CartLimitErrorPopup';
      popup.className = 'cart-limit-popup fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 hidden';
      popup.innerHTML = `
        <div class="cart-limit-popup-content bg-white rounded-lg p-6 max-w-md mx-4 relative">
          <button class="cart-limit-popup-close absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-2xl font-bold" aria-label="Kapat">&times;</button>
          <div class="text-center">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">${FoxTheme?.settings?.cartLimitPopupTitle}</h3>
            <p class="text-gray-600 mb-6">${FoxTheme?.settings?.cartLimitPopupMessage || 'Online mağazamızda satın alma limiti 150.000 TL\'dir. Daha yüksek tutarlı alımlarınız için sizi mağazalarımıza bekleriz.'}</p>
            <button class="cart-limit-popup-ok btn btn--primary w-full"><span class="btn__text">${FoxTheme?.settings?.cartLimitPopupButtonText || 'Tamam'}</span></button>
          </div>
        </div>
      `;
      document.body.appendChild(popup);
      
      // Add event listeners
      const closeBtn = popup.querySelector('.cart-limit-popup-close');
      const okBtn = popup.querySelector('.cart-limit-popup-ok');
      const overlay = popup;
      
      const hidePopup = () => {
        popup.classList.add('hidden');
        document.body.style.overflow = '';
      };

      const handleOkClick = () => {
        const buttonLink = FoxTheme?.settings?.cartLimitPopupButtonLink;
        if (buttonLink && buttonLink.trim() !== '') {
          window.location.href = buttonLink;
        } else {
          hidePopup();
        }
      };
      
      closeBtn.addEventListener('click', hidePopup);
      okBtn.addEventListener('click', handleOkClick);
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) hidePopup();
      });
      
      // Close with Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !popup.classList.contains('hidden')) {
          hidePopup();
        }
      });
    }
    
    // Show popup
    popup.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
    
    // Focus on OK button for accessibility
    setTimeout(() => {
      popup.querySelector('.cart-limit-popup-ok')?.focus();
    }, 100);
  }
}

customElements.define('cart-items', CartItems);

class CartRemoveItem extends HTMLAnchorElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();

      const cartItems = this.closest('cart-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-item', CartRemoveItem, { extends: 'a' });
class CartNote extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', FoxTheme.utils.debounce(this.onChange.bind(this), 300));

    if (this.button && this.cartNoteDetailsSummary) {
      this.button.addEventListener('click', () => {
        this.cartNoteDetailsSummary.close();
      });
    }

    const state = this.closest('.cart__addon');
    let cll;
    if(state) {
      cll = state.className;
    }

    if (window.innerWidth < 768 && !cll.includes('opened-mobile')) {
      window.addEventListener('DOMContentLoaded', () => {
        this.cartNoteDetailsSummary?.close();
      });
    } else if (window.innerWidth < 768 && cll.includes('opened-mobile') && !state.className.includes('active')){
      const summary = document.querySelector('.cart-note-anchor');
      if (summary) summary.click();
    }

    window.addEventListener('resize', FoxTheme.utils.debounce(() => {
      if (window.innerWidth < 768 && !cll.includes('opened-mobile')) {
        this.cartNoteDetailsSummary?.close();
      } else if(cll.includes('opened-desktop') && !state.className.includes('active')) {
          const summary = document.querySelector('.cart-note-anchor');
          if (summary) summary.click();
      } else if (!cll.includes('opened-desktop') && state.className.includes('active')){
          const summary = document.querySelector('.cart-note-anchor');
          if (summary) summary.click();
      } else if (window.innerWidth < 768 && cll.includes('opened-mobile') && !state.className.includes('active')) {
        const summary = document.querySelector('.cart-note-anchor');
        if (summary) summary.click();
      }
    }, 200));
  }

  get cartNoteDetailsSummary() {
    return this.closest('[is="accordion-details"]');
  }

  get button() {
    return this.querySelector('[type="button"]');
  }

  onChange(event) {
    const body = JSON.stringify({ note: event.target.value });
    fetch(`${FoxTheme.routes.cart_update_url}`, { ...FoxTheme.utils.fetchConfig(), ...{ body } });
  }
}

customElements.define('cart-note', CartNote);


class CartDiscount extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input');
    this.button = this.querySelector('.drawer-submit-discount');
    this.modal = this.closest('modal-component');

    this.button?.addEventListener('click', () => {
      const code = this.input?.value?.trim();
      if (!code) {
        this.showError(code);
        return;
      }

      this.applyDiscount(code);
    });

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target === this.input) {
        e.preventDefault();
        this.button?.click();
      }
    });
  }

  applyDiscount(code) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ discount: code })
    })
        .then(res => res.json())
        .then(cart => {
          const applied = cart.cart_level_discount_applications?.[0]?.amount;
          if (!applied) {
            this.showError(code);
            return;
          }
          this.hideError();
          const refreshEvent = new CustomEvent('cart:refresh', {
            detail: { open: true }
          });
          document.dispatchEvent(refreshEvent);
        })
        .catch(err => {
          console.error('Error applying discount:', err);
          this.showError(code);
        });
  }

  showError(code) {
    const error = this.querySelector('.cart__coupon--error');
    const errorVal = error?.querySelector('span');
    if (error && errorVal) {
      error.classList.remove('hidden');
      errorVal.textContent = code || '';
      setTimeout(() => {
        error.classList.add('hidden');
      }, 3000);
    }
  }

  hideError() {
    const error = this.querySelector('.cart__coupon--error');
    if (error) {
      error.classList.add('hidden');
    }
  }
}

customElements.define('cart-discount', CartDiscount);



class CalculateShipping extends ModalComponent {
  constructor() {
    super();
    this.countryProvince = this.querySelector('country-province');
    this.isCountrySetup = false;
  }
  static get observedAttributes() {
    return [...super.observedAttributes, 'data-show'];
  }
  show() {
    super.show();
    this.setAttribute('data-show', true);
  }

  hide() {
    super.hide();
    this.setAttribute('data-show', false);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === 'data-show' && newValue === 'true' && !this.isCountrySetup && this.countryProvince) {
      const template = this.countryProvince.querySelector('template');
      const templateContent = document.importNode(template.content, true);
      this.countryProvince.countryElement.appendChild(templateContent);
      this.countryProvince.init();
      this.isCountrySetup = true;
    }
  }
}
customElements.define('calculate-shipping', CalculateShipping);
class CountryProvinceForm extends HTMLElement {
  constructor() {
    super();
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.template = this.dataset.template;
    this.countryElement.addEventListener('change', this.handleCountryChange.bind(this));
    if (this.template && this.template === 'cart') {
      this.init();
    }
  }

  init() {
    if (this.getAttribute('country') !== '') {
      this.countryElement.selectedIndex = Math.max(
        0,
        Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.dataset.country)
      );
      this.countryElement.dispatchEvent(new Event('change'));
    } else {
      this.handleCountryChange();
    }
  }

  handleCountryChange() {
    const option = this.countryElement.options[this.countryElement.selectedIndex],
      provinces = JSON.parse(option.dataset.provinces);

    this.provinceElement.closest('.form-field').hidden = provinces.length === 0;

    if (provinces.length === 0) {
      return;
    }

    this.provinceElement.innerHTML = '';

    provinces.forEach((data) => {
      const selected = data[1] === this.dataset.province;
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
}
customElements.define('country-province', CountryProvinceForm);

class ShippingCalculator extends HTMLFormElement {
  constructor() {
    super();

    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;

    this.submitButton.addEventListener('click', this.handleFormSubmit.bind(this));
  }

  handleFormSubmit(event) {
    event.preventDefault();

    const zip = this.querySelector('[name="address[zip]"]').value,
      country = this.querySelector('[name="address[country]"]').value,
      province = this.querySelector('[name="address[province]"]').value;

    this.submitButton.classList.add('btn--loading');

    const body = JSON.stringify({
      shipping_address: { zip, country, province },
    });
    let sectionUrl = `${FoxTheme.routes.cart_url}/shipping_rates.json`;

    sectionUrl = sectionUrl.replace('//', '/');

    fetch(sectionUrl, { ...FoxTheme.utils.fetchConfig('javascript'), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.shipping_rates) {
          this.formatShippingRates(parsedState.shipping_rates);
        } else {
          this.formatError(parsedState);
        }
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.resultsElement.hidden = false;
        this.submitButton.classList.remove('btn--loading');
      });
  }

  formatError(errors) {
    const shippingRatesList = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert alert--error blocks-radius grid gap-2">
        <p class="font-body-bolder m-0">${FoxTheme.shippingCalculatorStrings.error}</p>
        <ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>
      </div>
    `;
  }

  formatShippingRates(shippingRates) {
    const shippingRatesList = shippingRates.map(({ presentment_name, currency, price }) => {
      return `<li>${presentment_name}: ${currency} ${price}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="alert blocks-radius alert--${
        shippingRates.length === 0 ? 'error' : 'success'
      } grid gap-2 leading-tight">
        <p class="font-body-bolder m-0">${
          shippingRates.length === 0
            ? FoxTheme.shippingCalculatorStrings.notFound
            : shippingRates.length === 1
            ? FoxTheme.shippingCalculatorStrings.oneResult
            : FoxTheme.shippingCalculatorStrings.multipleResults
        }</p>
        ${
          shippingRatesList === ''
            ? ''
            : `<ul class="list-disc grid gap-1 text-sm" role="list">${shippingRatesList.join('')}</ul>`
        }
      </div>
    `;
  }
}
customElements.define('shipping-calculator', ShippingCalculator, { extends: 'form' });

class CartDrawerProductsRecommendation extends HTMLElement {
  constructor() {
    super();
    this.carousel = null;
    this.isLoading = false;

    this.classes = {
      grid: 'f-grid',
      swiper: 'swiper',
      swiperWrapper: 'swiper-wrapper',
      loading: 'is-loading',
    };

    // Handle Safari separately
    const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');
    if (isSafari) {
      this.init();
    } else {
      FoxTheme.Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
  }

  disconnectedCallback() {
    // Cleanup
    this.destroyCarousel();
    const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
    mql.removeEventListener('change', this.handleCarousel.bind(this));
  }

  init() {
    if (this.isLoading || !this.dataset.url) return;

    this.isLoading = true;
    this.classList.add(this.classes.loading);

    fetch(this.dataset.url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (!sectionInnerHTML) return;

        const recommendations = sectionInnerHTML.querySelector('cart-drawer-products-recommendation');
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
          mql.addEventListener('change', this.handleCarousel.bind(this));
          this.handleCarousel();
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        } else {
          this.closest('.shopify-section')?.remove();
        }
      })
      .catch((error) => {
        console.error('Error loading recommendations:', error);
        this.classList.add('has-error');
      })
      .finally(() => {
        this.isLoading = false;
        this.classList.remove(this.classes.loading);
      });
  }

  initCarousel() {
    if (!this.wrapper?.innerHTML.trim().length) {
      this.classList.add('hidden');
      return;
    }

    try {
      this.container.classList.add(this.classes.swiper);
      this.wrapper.classList.remove(this.classes.grid);
      this.wrapper.classList.add(this.classes.swiperWrapper);

      this.carousel = new FoxTheme.Carousel(this.slideContainer, {
        slidesPerView: 3,
        spaceBetween: 8,
        loop: false,
        navigation: {
          nextEl: this.sliderNext,
          prevEl: this.sliderPrev,
        },
      });

      if (this.carousel) {
        this.carousel.init();
        this.calcNavButtonsPosition();
      }
    } catch (error) {
      console.error('Error initializing carousel:', error);
      this.destroyCarousel();
    }
  }

  handleCarousel() {
    if (FoxTheme.config.mqlMobile) {
      this.destroyCarousel();
    } else {
      this.initCarousel();
    }
  }

  destroyCarousel() {
    if (!this.wrapper?.innerHTML.trim().length) {
      this.classList.add('hidden');
      return;
    }
    this.container?.classList.remove(this.classes.swiper);
    this.wrapper?.classList.remove(this.classes.swiperWrapper);
    this.wrapper?.classList.add(this.classes.grid);

    // Only try to destroy if carousel exists and is an object
    if (this.carousel && typeof this.carousel === 'object' && this.carousel.slider) {
      this.carousel.slider.destroy();
      this.carousel = null;
    }
  }

  calcNavButtonsPosition() {
    if (!this.dataset.calcButtonPosition === 'true') return;
    const firstMedia = this.querySelector('.product-card__image-wrapper');
    if (firstMedia && firstMedia.clientHeight > 0) {
      this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
    }
  }

  get slideContainer() {
    return this.querySelector('.swiper');
  }

  get sliderPagination() {
    return this.querySelector('.swiper-pagination');
  }

  get sliderNext() {
    return this.querySelector('.swiper-button-next');
  }

  get sliderPrev() {
    return this.querySelector('.swiper-button-prev');
  }

  get container() {
    return this.querySelector('.cart-drawer-products-recommendation__container');
  }

  get wrapper() {
    return this.querySelector('.cart-drawer-products-recommendation__wrapper');
  }
}

customElements.define('cart-drawer-products-recommendation', CartDrawerProductsRecommendation);

class MainCart extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('cart:grouped-sections', this.getSectionToRender.bind(this));
    
    // Initialize terms and conditions functionality
    this.initTermsAndConditions();
  }

  getSectionToRender(event) {
    event.detail.sections.push(FoxTheme.utils.getSectionId(this));
  }

  initTermsAndConditions() {
    const termsCheckbox = this.querySelector('#terms-and-conditions-main');
    const checkoutButton = this.querySelector('.btn.btn--primary.w-full');
    
    if (!termsCheckbox || !checkoutButton) return;
    
    // Get popup elements
    const popup = document.querySelector(`#TermsWarningPopup-${FoxTheme.utils.getSectionId(this)}`);
    const popupOverlay = popup?.querySelector('.terms-warning-overlay');
    const popupCloseBtn = popup?.querySelector('.terms-warning-close');
    
    // Function to show popup
    const showTermsWarningPopup = () => {
      if (popup) {
        popup.classList.remove('hidden');
        document.body.style.overflow = 'hidden';
        
        // Focus on close button for accessibility
        setTimeout(() => {
          popupCloseBtn?.focus();
        }, 100);
      } 
    };
    
    // Function to hide popup
    const hideTermsWarningPopup = () => {
      if (popup) {
        popup.classList.add('hidden');
        document.body.style.overflow = '';
      }
    };
    
    // Function to update button state
    const updateButtonState = () => {
      if (termsCheckbox.checked) {
        checkoutButton.disabled = false;
        checkoutButton.style.opacity = '1';
        checkoutButton.style.cursor = 'pointer';
        checkoutButton.style.pointerEvents = 'auto';
      } else {
        // Don't disable the button - instead keep it enabled but prevent default action
        checkoutButton.disabled = false;
        checkoutButton.style.opacity = '0.5';
        checkoutButton.style.cursor = 'not-allowed';
        checkoutButton.style.pointerEvents = 'auto';
      }
    };
    
    // Set initial state
    updateButtonState();
    
    // Add event listener for checkbox changes
    termsCheckbox.addEventListener('change', updateButtonState);
    
    // Add event listeners for popup
    if (popup) {
      // Close popup when clicking close button
      popupCloseBtn?.addEventListener('click', hideTermsWarningPopup);
      
      // Close popup when clicking overlay
      popupOverlay?.addEventListener('click', hideTermsWarningPopup);
      
      // Close popup with Escape key
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !popup.classList.contains('hidden')) {
          hideTermsWarningPopup();
        }
      });
    }
    
    // Add click event listener to checkout button for popup
    checkoutButton.addEventListener('click', (event) => {
      if (!termsCheckbox.checked) {
        event.preventDefault();
        event.stopPropagation();
        showTermsWarningPopup();
        return false;
      }
    });
    
    // Prevent form submission if terms are not accepted
    const form = checkoutButton.closest('form') || document.getElementById('cart');
    if (form) {
      form.addEventListener('submit', (event) => {
        if (!termsCheckbox.checked) {
          event.preventDefault();
          event.stopPropagation();
          showTermsWarningPopup();
          return false;
        }
      });
    }
  }
}
customElements.define('main-cart', MainCart);

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };
    this.goal = Number(this.dataset.minimumAmount) * Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector('progress-bar');
    this.money_format = window.FoxTheme.settings.moneyFormat;
  }

  connectedCallback() {
    this.updateShippingGloal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:updated', (event) => {
      this.updateShippingGloal(event.detail.cart.items_subtotal_price);
    });
  }

  updateShippingGloal(amount) {
    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (this.percent >= 100) this.percent = 100;

    if (this.cartTotal >= this.goal) {
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      this.querySelector(this.selectors.leftToSpend).innerHTML = FoxTheme.Currency.formatMoney(
        spend,
        this.money_format
      );
      this.classList.remove('free-shipping-goal--done');
      this.progress.style.setProperty('--percent', `${this.percent}%`);
      this.progress.dataset.value = this.cartTotal;
      this.progress.dataset.max = this.goal;
    }
  }
}
customElements.define('free-shipping-goal', FreeShippingGoal);

customElements.define('coupone-code', class extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('.coupon-input');
    this.button = this.querySelector('.coupon-button');
    this.message = this.querySelector('.coupon-message');

    this.button.addEventListener('click', this.onApply.bind(this));
  }

  async onApply() {
    const couponCode = this.input.value.trim();
    if (!couponCode) {
      this.showMessage('Please enter a code.', true);
      return;
    }

    const body = JSON.stringify({ discount: couponCode });

    const cartJson = await (
        await fetch(`${FoxTheme.routes.cart_url}`, {
          ...FoxTheme.utils.fetchConfig(),
          body
        })
    ).json();
    cartJson['sections'] = payload['sections']
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson })
    // FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });


  }

  showMessage(text, isError) {
    this.message.textContent = text;
    this.message.style.color = isError ? 'red' : 'green';
  }
});



window.FoxKitAddToCart = async (payload) => {
  if (!payload?.properties?.['_FoxKit offer']) return;

  const cartJson = await (
    await fetch(`${FoxTheme.routes.cart_url}`, {
      ...FoxTheme.utils.fetchConfig(),
    })
  ).json();
  cartJson['sections'] = payload['sections'];
  FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

  document.dispatchEvent(
    new CustomEvent('product-ajax:added', {
      detail: {
        product: payload,
      },
    })
  );
};

function updateCart(code) {
  fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      discount: code
    })
  })
      .then(res => res.json())
      .then(cart => {
        const discountsWrap = document.querySelector('.discounts--wrap');
        const coupon_input = document.querySelector('.coupon__field');
        const error = document.querySelector('.cart__coupon--error');
        const errorVal = document.querySelector('.cart__coupon--error span');
        if(!cart.cart_level_discount_applications || !cart.cart_level_discount_applications[0] || !cart.cart_level_discount_applications[0].amount) {
          if(error && errorVal){
            error.classList.remove('hidden');
            errorVal.innerHTML = code;
            setTimeout(()=>{
              error.classList.add('hidden');
            }, 3000)
          }

          return
        }
        const saving_val = cart.cart_level_discount_applications[0].amount;
        if(discountsWrap && code != "" && code != null && code != undefined) {
          cart.cart_level_discount_applications.forEach((item)=>{
            let existVal = false;
            const exist = document.querySelectorAll('.discounts--wrap .discount');
            if(exist){
              exist.forEach((item)=>{
                if(item.dataset.discount == code) {
                  existVal = true;
                }
              })
            }
            if(!existVal){
              const label = `
              <li class="discount text-sm-extra font-body-bold flex items-center" data-discount="${item.discount_application.title}">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="icon icon-discount icon--extra-small"
                  viewBox="0 0 15 15"
                  fill="none"
                >
                  <path d="M2.31547 7.56547C2.23358 7.48349 2.18755 7.37236 2.1875 7.25648V2.1875H7.25648C7.37236 2.18755 7.48349 2.23358 7.56547 2.31547L12.997 7.74703C13.079 7.82907 13.1251 7.94031 13.1251 8.05629C13.1251 8.17227 13.079 8.28351 12.997 8.36555L8.36719 12.997C8.28515 13.079 8.17391 13.1251 8.05793 13.1251C7.94195 13.1251 7.83071 13.079 7.74867 12.997L2.31547 7.56547Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4.59375 5.25C4.95619 5.25 5.25 4.95619 5.25 4.59375C5.25 4.23131 4.95619 3.9375 4.59375 3.9375C4.23131 3.9375 3.9375 4.23131 3.9375 4.59375C3.9375 4.95619 4.23131 5.25 4.59375 5.25Z" fill="currentColor"/>
                </svg>
                <span class="discount-title">${item.discount_application.title}</span>
                <span class="font-body-bold discount-saving">(-${
                  FoxTheme.Currency.formatMoney(
                      item.discount_application.total_allocated_amount,
                      window.FoxTheme.settings.moneyFormat
                  )})</span>
              </li>`;
              discountsWrap.classList.remove('hidden');
              discountsWrap.insertAdjacentHTML('beforeend', label);
              coupon_input.value = "";
            }
          })
        } else {
          discountsWrap.classList.add('hidden');
        }
      })
      .catch(err => {
        console.error('Error applying discount:', err);
      });
}

window.addEventListener('DOMContentLoaded', () => {
  const apply_coupon = document.querySelector('.coupon__button');
  const coupon_input = document.querySelector('.coupon__field');

  if(apply_coupon){
    apply_coupon.addEventListener('click', ()=>{
      if(coupon_input) {
        if(coupon_input.value != ""){
          updateCart(coupon_input.value);
        }

      }
    })
  }

  const codeInput = document.getElementById('cart-coupon');
  const codeButton = document.querySelector('.coupon__button');
  function toggleButtonState() {
    if(codeInput){
      if(codeInput.value){
        if (codeInput.value.trim() === '') {
          codeButton.setAttribute('disabled', 'disabled');
        } else {
          codeButton.removeAttribute('disabled');
        }
      }
    }
  }
  toggleButtonState();
  if(codeInput){
    codeInput.addEventListener('input', toggleButtonState);
  }
});